import { useState, useRef, useCallback, useEffect } from "react";
import { storage } from "@/lib/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { deleteField } from "firebase/firestore";
import { toast } from "sonner";
import { Upload, X, Plus, Check, ChevronsUpDown } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext.js";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tenancy, useTenancy } from "@/contexts/TenancyContext";
import { getUserInfo } from "@/services/userService";
import { BeautifulTabsSelector } from "@/components/ui/BeautifulTabsSelector";
interface BusinessUnit {
  name: string;
  dateAdded: { _seconds: number; _nanoseconds: number } | { __time__: string };
  lastUpdated: { _seconds: number; _nanoseconds: number } | { __time__: string };
  lastUpdatedBy: string;
}

type ExtendedTenancy = Tenancy & {
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  logo?: string;
  adminName?: string;
  adminEmail?: string;
  abnAcn?: string;
  anzsic?: string[];
  website?: string;
  customerOrg?: {
    teams?: {
      businessUnitNames?: Record<string, BusinessUnit>;
      teamNames?: Record<string, BusinessUnit>;
    };
  };
};
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { getCompanyInfo, getCompanySettings, saveCompanyInfo } from "@/services/companyService";
import { getCompanyContext, updateCompanyContext } from "@/services/companyContextService";
import CountrySearchCombobox from "@/components/ui/CountrySearchCombobox";
import { getAllCountries, getPopularCountries } from "@/utils/country-utils";
import AnzsicSearchCombobox from "@/components/ui/AnzsicSearchCombobox";

export default function GeneralSection() {
  const { isDark } = useTheme();

  const [legalName, setLegalName] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [abnAcn, setAbnAcn] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('');
  const [availableCountries, setAvailableCountries] = useState<string[]>([]);
  const [popularCountries, setPopularCountries] = useState<string[]>([]);
  const [website, setWebsite] = useState('');
  const [businessUnits, setBusinessUnits] = useState<string[]>([]);
  const [selectedTab, setSelectedTab] = useState("businessDetails");
  
  const [availableBusinessUnits, setAvailableBusinessUnits] = useState<string[]>([
    'IT',
    'Finance',
    'HR',
    'Operations',
    'Marketing',
    'Sales',
    'Customer Support',
    'Product',
    'Engineering',
    'Design'
  ]);
    const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [employeeCount, setEmployeeCount] = useState('');
  const [officeCount, setOfficeCount] = useState('');
  const [mainContactEmail, setMainContactEmail] = useState('');
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  // Initialize with empty array to prevent null reference errors
  const [selectedAnzsic, setSelectedAnzsic] = useState<string[]>(() => {
    // If we want to load initial values from props or context in the future, we can do it here
    return [];
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSingleAnzsicSelect = useCallback((value: string) => {
    console.log("ANZSIC selected - before state update:", { value, current: selectedAnzsic });
    setSelectedAnzsic(prev => {
      // Ensure prev is an array (handle null/undefined cases)
      const currentSelections = Array.isArray(prev) ? prev : [];
      
      // For multi-select, add to existing selections if not already present
      if (currentSelections.includes(value)) {
        return currentSelections; // Already selected, no change needed
      }
      const newSelection = [...currentSelections, value];
      console.log("ANZSIC selected - new state:", newSelection);
      return newSelection;
    });
  }, [selectedAnzsic]);
  
  const handleSingleAnzsicDeselect = useCallback((value: string) => {
    console.log("ANZSIC deselected - before state update:", { value, current: selectedAnzsic });
    setSelectedAnzsic(prev => {
      // Remove the deselected value from the array
      const newSelection = prev.filter(v => v !== value);
      console.log("ANZSIC deselected - new state:", newSelection);
      return newSelection;
    });
  }, [selectedAnzsic]);
  

  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelection(file);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelection(file);
    }
  };

  const handleFileSelection = (file: File) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml'];
    if (validTypes.includes(file.type)) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      toast.error('Please select a valid image file (JPEG, JPG, PNG, or SVG)');
    }
  };

  const handleLogoClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemoveLogo = useCallback(() => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleAddBusinessUnit = (unit: string) => {
    if (!unit.trim()) return;

    const newUnit = unit.trim();

    // Add to selected units if not already selected
    if (!businessUnits.includes(newUnit)) {
      setBusinessUnits([...businessUnits, newUnit]);
    }

    // Add to available units if it's a new one
    if (!availableBusinessUnits.includes(newUnit)) {
      setAvailableBusinessUnits([...availableBusinessUnits, newUnit]);
    }

    setInputValue('');
  };

  const handleRemoveBusinessUnit = (unit: string) => {
    setBusinessUnits(businessUnits.filter(bu => bu !== unit));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      handleAddBusinessUnit(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && businessUnits.length > 0) {
      // Remove the last unit on backspace when input is empty
      setBusinessUnits(prev => prev.slice(0, -1));
    }
  };

  const filteredBusinessUnits = availableBusinessUnits.filter(
    unit => !businessUnits.includes(unit) &&
      unit.toLowerCase().includes(inputValue.toLowerCase())
  );

  const { activeTenancy, setActiveTenancy } = useTenancy();
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Form validation
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!legalName.trim()) {
      errors.legalName = 'Legal name is required';
    }

    if (!tradeName.trim()) {
      errors.tradeName = 'Trading name is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save business details handler
  const handleSaveBusinessDetails = async () => {
    if (!validateForm()) {
      return;
    }

    console.log('Starting save with business units:', businessUnits);

    setIsSaving(true);
    const now = new Date();
    const timestamp = {
      _seconds: Math.floor(now.getTime() / 1000),
      _nanoseconds: now.getMilliseconds() * 1000000
    };

    try {
      // Get the current user ID for lastUpdatedBy
      const user = await getUserInfo();
      const userId = user?.uid || 'system';

      // Get existing business units
      const existingUnits = (activeTenancy as ExtendedTenancy)?.customerOrg?.teams?.businessUnitNames || {};
      console.log('Existing units from tenancy:', existingUnits);

      // Create a map of existing units by name for quick lookup
      const existingUnitsByName = new Map<string, { id: string; unit: BusinessUnit }>();

      // First, build a map of existing units by name
      Object.entries(existingUnits).forEach(([key, unit]) => {
        if (/^\d+$/.test(key) && unit && typeof unit === 'object' && 'name' in unit) {
          const { id, ...unitWithoutId } = unit as any;
          existingUnitsByName.set(unit.name.toLowerCase(), { id: key, unit: unitWithoutId });
        }
      });

      console.log('Existing units by name:', Object.fromEntries(existingUnitsByName));

      // Create a set of the current business unit names (case-insensitive)
      const currentBusinessUnitNames = new Set(businessUnits.map(bu => bu.trim().toLowerCase()));
      console.log('Current business unit names:', Array.from(currentBusinessUnitNames));

      // Find the next available ID
      let nextId = 1;
      const existingIds = Object.keys(existingUnits)
        .filter(key => /^\d+$/.test(key))
        .map(Number);

      if (existingIds.length > 0) {
        nextId = Math.max(...existingIds) + 1;
      }

      // Process the currently selected business units
      const updatedUnits: Record<string, BusinessUnit> = {};
      const currentUnitNames = new Set<string>();

      // First, find all existing unit names to track deletions
      Object.entries(existingUnits).forEach(([key, unit]) => {
        if (/^\d+$/.test(key) && unit && typeof unit === 'object' && 'name' in unit) {
          currentUnitNames.add(unit.name.toLowerCase());
        }
      });

      // Process selected units
      for (const buName of businessUnits) {
        const normalizedBuName = buName.trim();
        const existing = existingUnitsByName.get(normalizedBuName.toLowerCase());

        if (existing) {
          // Use existing unit with its original ID
          updatedUnits[existing.id] = {
            ...existing.unit,
            name: normalizedBuName,
            lastUpdated: timestamp,
            lastUpdatedBy: userId
          };
          console.log(`Updating existing unit ${existing.id}:`, updatedUnits[existing.id]);

          // Remove from currentUnitNames to track which ones should be kept
          currentUnitNames.delete(normalizedBuName.toLowerCase());
        } else {
          // Find next available ID for new units
          while (updatedUnits[nextId.toString()] !== undefined || existingUnits[nextId.toString()] !== undefined) {
            nextId++;
          }

          const newId = nextId.toString();
          updatedUnits[newId] = {
            name: normalizedBuName,
            dateAdded: timestamp,
            lastUpdated: timestamp,
            lastUpdatedBy: userId
          };
          console.log(`Adding new unit ${newId}:`, updatedUnits[newId]);
          nextId++;
        }
      }

      console.log('Final units to save:', updatedUnits);

      // Get current company info
      const companyInfo = await getCompanyInfo(activeTenancy?.id);

      // Prepare the complete company data with updated business units and other info
      const companyUpdate: any = {
        // Include all company info fields
        ...companyInfo,
        adminName: adminName.trim(),
        adminEmail: adminEmail.trim(),
        customerName: legalName.trim(),
        customerFriendlyName: tradeName.trim(),
        abnAcn: abnAcn.trim(),
        address: address.trim(),
        city: city.trim(),
        state: state.trim(),
        zip: zip.trim(),
        country: country.trim(),
        lastUpdated: timestamp,
        lastUpdatedBy: userId,
        anzsic: selectedAnzsic,
        website: website.trim()
      };

      // Add customerOrg with type assertions to handle the extended type
      if (activeTenancy) {
        const extendedTenancy = activeTenancy as ExtendedTenancy;
        companyUpdate.customerOrg = {
          ...extendedTenancy.customerOrg,
          teams: {
            ...extendedTenancy.customerOrg?.teams,
            businessUnitNames: updatedUnits
          }
        };
      }

      console.log('Saving complete company data with business units:', companyUpdate);
      await saveCompanyInfo(activeTenancy?.id, {
        set: companyUpdate,
        merge: true // Use merge: false to ensure complete replacement
      });
      
      // Save company context data (employee count and main contact)
      if (activeTenancy?.id) {
        const companyContextUpdates: any = {
          metrics: {
            employeeCount: employeeCount ? parseInt(employeeCount) : null,
            officeCount: officeCount ? parseInt(officeCount) : null
          },
          mainContact: {
            email: mainContactEmail.trim() || null
          },
          'metadata.updatedAt': timestamp,
          'metadata.updatedBy': userId
        };
        
        // Remove null values
        if (!companyContextUpdates.metrics.employeeCount) {
          delete companyContextUpdates.metrics.employeeCount;
        }
        if (!companyContextUpdates.metrics.officeCount) {
          delete companyContextUpdates.metrics.officeCount;
        }
        if (!companyContextUpdates.mainContact.email) {
          delete companyContextUpdates.mainContact.email;
        }
        if (Object.keys(companyContextUpdates.metrics).length === 0) {
          delete companyContextUpdates.metrics;
        }
        if (Object.keys(companyContextUpdates.mainContact).length === 0) {
          delete companyContextUpdates.mainContact;
        }
        
        if (Object.keys(companyContextUpdates).length > 2) { // More than just metadata
          await updateCompanyContext(activeTenancy.id, companyContextUpdates, userId);
        }
      }
      
      console.log('Company info and business units saved successfully');

      // Update active tenancy with the new values
      if (activeTenancy) {
        console.log('Updating active tenancy...');
        const updatedTenancy: ExtendedTenancy = {
          ...activeTenancy,
          adminName: adminName.trim(),
          adminEmail: adminEmail.trim(),
          legalName: legalName.trim(),
          name: tradeName.trim(),
          abnAcn: abnAcn.trim(),
          address: address.trim(),
          city: city.trim(),
          state: state.trim(),
          zip: zip.trim(),
          country: country.trim(),
          anzsic: selectedAnzsic,
          website: website.trim(),
          customerOrg: {
            ...(activeTenancy as ExtendedTenancy).customerOrg,
            teams: {
              ...(activeTenancy as ExtendedTenancy).customerOrg?.teams,
              businessUnitNames: updatedUnits
            }
          }
        };
        setActiveTenancy(updatedTenancy as Tenancy);
      }

      toast.success('Business details updated successfully');
    } catch (error) {
      console.error('Error saving business details:', error);
      toast.error('Failed to update business details');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAdminDetails = async () => {
    setIsSaving(true);
    try {
      const companyUpdate: any = {
        adminName: adminName.trim(),
        adminEmail: adminEmail.trim()
      };

      await saveCompanyInfo(activeTenancy?.id, {
        set: companyUpdate,
        merge: true
      });
      console.log('Company admin details saved successfully');
      toast.success('Admin details updated successfully');
    } catch (error) {
      console.error('Error saving admin details:', error);
      toast.error('Failed to update admin details');
    } finally {
      setIsSaving(false);
    }
  };

  // Save company logo handler
  const saveCompanyLogo = async () => {
    if (!logoFile || !activeTenancy) return;

    setIsUploading(true);
    try {
      const companyID = window.companyID;
      const storageRef = ref(storage, `companyLogos/${companyID}/${companyID}_logo.png`);

      // Upload the file
      await uploadBytes(storageRef, logoFile);

      // Get the download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Update Firestore
      await saveCompanyInfo(activeTenancy?.id, {
        set: {
          logo: `companyLogos/${companyID}/${companyID}_logo.png`
        },
        merge: true
      });

      // Update the active tenancy with the new logo URL
      setActiveTenancy({
        ...activeTenancy,
        logo: downloadURL
      });

      toast.success('Company logo updated successfully');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload company logo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCountryChange = (countries: string[]) => {
    setCountry(countries[0]);
  };

  useEffect(() => {
    if (activeTenancy) {
      const extendedTenancy = activeTenancy as ExtendedTenancy;
      getCompanySettings().then(settings => {
        getCompanyInfo().then(async (info) => {
          const popularCountries = getPopularCountries();
          setPopularCountries(popularCountries);
          const countries = getAllCountries();
          setAvailableCountries(countries);

          console.log('[CompanySettings] Available countries', availableCountries);
          console.log('[CompanySettings] Popular countries', popularCountries);
          console.log('Company info', info);
          console.log('Company settings', settings);
          for (const key in info) {
            extendedTenancy[key] = info[key];
          }

          const businessUnitNames = (info.customerOrg?.teams?.businessUnitNames || {}) as Record<string, { name: string }>;
          const businessUnitList = Object.values(businessUnitNames).map(bu => bu.name);
          console.log('Extended tenancy', extendedTenancy);
          setAdminName(extendedTenancy.adminName || '');
          setAdminEmail(extendedTenancy.adminEmail || '');
          setLogoPreview(extendedTenancy.logo || '');
          setBusinessUnits(businessUnitList);
          setLegalName(extendedTenancy.legalName || '');
          setTradeName(extendedTenancy.name || '');
          setAbnAcn(extendedTenancy.abnAcn || '');
          setAddress(extendedTenancy.address || '');
          setCity(extendedTenancy.city || '');
          setState(extendedTenancy.state || '');
          setZip(extendedTenancy.zip || '');
          setCountry(extendedTenancy.country || '');
          setSelectedAnzsic(extendedTenancy.anzsic || null);
          
          // DED-1511: Check for website in main document first, then fall back to companyContext
          let websiteValue = extendedTenancy.website || info?.website || '';
          if (!websiteValue && activeTenancy?.id) {
            try {
              const companyContext = await getCompanyContext(activeTenancy.id);
              if (companyContext?.social?.website) {
                websiteValue = companyContext.social.website;
                console.log('[CompanySettings] Website loaded from companyContext:', websiteValue);
              } else if (companyContext?.identity?.domain) {
                // Fall back to domain from identity if social.website not set
                websiteValue = companyContext.identity.domain;
                console.log('[CompanySettings] Website loaded from companyContext.identity.domain:', websiteValue);
              }
            } catch (err) {
              console.warn('[CompanySettings] Failed to load companyContext:', err);
            }
          }
          setWebsite(websiteValue);
          
          // Load company context data for employee count and main contact
          if (activeTenancy?.id) {
            try {
              const companyContext = await getCompanyContext(activeTenancy.id);
              if (companyContext) {
                setEmployeeCount(companyContext.metrics?.employeeCount?.toString() || '');
                setOfficeCount(companyContext.metrics?.officeCount?.toString() || '');
                // Type assertion for mainContact since it's not in the original interface
                setMainContactEmail((companyContext as any).mainContact?.email || '');
                console.log('[CompanySettings] Loaded company context data:', {
                  employeeCount: companyContext.metrics?.employeeCount,
                  officeCount: companyContext.metrics?.officeCount,
                  mainContactEmail: (companyContext as any).mainContact?.email
                });
              }
            } catch (err) {
              console.warn('[CompanySettings] Failed to load company context:', err);
            }
          }
        });
      });
    }
  }, [activeTenancy]);

  const TAB_GROUPS = [
    {
      group: "Business",
      type: "Business",
      options: [
        { value: "businessDetails", label: "Business Details" },
        { value: "adminDetails", label: "Admin Details" },
      ],
    }
  ];
  
  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-6">
        {/* Tabs */}
        <Tabs defaultValue="businessDetails" value={selectedTab} className="w-full">
        {/* <h2 className="text-xl font-semibold mb-4 text-foreground">General Settings</h2> */}
        <BeautifulTabsSelector
            options={TAB_GROUPS}
            value={selectedTab}
            onChange={setSelectedTab}
            selectorLabel="General"
          />
        <p className="text-sm text-muted-foreground mb-6">Manage your company's core details and preferences.</p>

        
  
          {/* Business Details Tab Content */}
          <TabsContent value="businessDetails" className="space-y-4">
            {/* Company Logo Section */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Company Logo
              </label>
              <div className="flex items-center gap-4">
                <div
                  className="relative group"
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={handleLogoClick}
                >
                  <div className={`w-24 h-24 rounded-lg overflow-hidden ${logoPreview ? 'bg-transparent' : 'bg-muted'} flex items-center justify-center border-2 ${isDragging ? 'border-primary' : 'border-border'} ${!logoPreview ? 'border-dashed' : ''} transition-colors`}>
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Company logo"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center p-2 text-center">
                        {activeTenancy?.logo && activeTenancy.logo !== '' && activeTenancy.logo !== null ? (
                          <img
                            src={activeTenancy.logo}
                            alt="Company logo"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Upload className="h-6 w-6 text-muted-foreground mb-1" />
                        )}
                        <span className="text-xs text-muted-foreground">
                          {isDragging ? 'Drop here' : 'Click or drag'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="absolute -right-2 -bottom-2 bg-primary text-primary-foreground rounded-full p-1.5 cursor-pointer hover:bg-primary/90 transition-colors opacity-0 group-hover:opacity-100">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-camera"
                    >
                      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
                      <circle cx="12" cy="13" r="3" />
                    </svg>
                    <span className="sr-only">Upload logo</span>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="sr-only"
                    accept="image/png, image/jpeg, image/jpg, image/svg+xml"
                    onChange={handleFileChange}
                  />
                </div>
                <div className="flex-1">
                  <div className="text-xs text-muted-foreground">
                    <p>SVG, PNG, JPG (max. 2MB)</p>
                    <p>Recommended size: 400x400px</p>
                  </div>
                  {logoFile && (
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        onClick={saveCompanyLogo}
                        disabled={isUploading}
                        className={`px-3 py-1.5 text-xs rounded-md flex items-center gap-1.5 ${isUploading
                            ? 'bg-muted text-muted-foreground cursor-not-allowed'
                            : 'bg-primary text-primary-foreground hover:bg-primary/90'
                          }`}
                      >
                        {isUploading ? (
                          <>
                            <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="h-3 w-3" />
                            Upload
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5"
                      >
                        <X className="h-3 w-3" />
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <label htmlFor="customerNameInput" className="block text-sm font-medium text-foreground">
                Company Legal Name
              </label>
              <input
                type="text"
                id="customerNameInput"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter company legal name"
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="customerFriendlyNameInput" className="block text-sm font-medium text-foreground">
                Company Trading Name
              </label>
              <input
                type="text"
                id="customerFriendlyNameInput"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter company trading name"
                value={tradeName}
                onChange={(e) => setTradeName(e.target.value)}
              />
            </div>

            {/* ABN/ACN */}
            <div className="space-y-2">
              <label htmlFor="abnAcnInput" className="block text-sm font-medium text-foreground">
                ABN / ACN
              </label>
              <input
                type="text"
                id="abnAcnInput"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="e.g., 12 345 678 901"
                value={abnAcn}
                onChange={(e) => setAbnAcn(e.target.value)}
              />
            </div>

            {/* ANZSIC Code */}
            <div className="space-y-2">
              <label htmlFor="anzsicCodeInput" className="block text-sm font-medium text-foreground">
                ANZSIC Code
              </label>
              <AnzsicSearchCombobox
                selected={selectedAnzsic}
                onSelect={handleSingleAnzsicSelect}
                onDeselect={handleSingleAnzsicDeselect}
                placeholder="Search ANZSIC codes..."
                singleSelect={false} // Enable multi-select
              />
            </div>

            {/* Website */}
            <div className="space-y-2">
              <label htmlFor="websiteInput" className="block text-sm font-medium text-foreground">
                Website
              </label>
              <input
                type="text"
                id="websiteInput"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter company website URL"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="addressInput" className="block text-sm font-medium text-foreground">
                Address
              </label>
              <input
                type="text"
                id="addressInput"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter company address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="cityInput" className="block text-sm font-medium text-foreground">
                City
              </label>
              <input
                type="text"
                id="cityInput"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter company city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="stateInput" className="block text-sm font-medium text-foreground">
                State
              </label>
              <input
                type="text"
                id="stateInput"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter company state"
                value={state}
                onChange={(e) => setState(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="zipInput" className="block text-sm font-medium text-foreground">
                Post Code
              </label>
              <input
                type="text"
                id="zipInput"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter zip code"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="countryInput" className="block text-sm font-medium text-foreground">
                Country
              </label>
              <CountrySearchCombobox
                options={availableCountries}
                selected={[country]}
                setSelected={handleCountryChange}
                placeholder="Select country..."
                popularOptions={popularCountries}
                singleSelect
              />
            </div>

            {/* Company Size Section */}
            <div className="space-y-2">
              <label htmlFor="employeeCountInput" className="block text-sm font-medium text-foreground">
                Number of Employees
              </label>
              <input
                type="number"
                id="employeeCountInput"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter number of employees"
                value={employeeCount}
                onChange={(e) => setEmployeeCount(e.target.value)}
                min="0"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="officeCountInput" className="block text-sm font-medium text-foreground">
                Number of Offices
              </label>
              <input
                type="number"
                id="officeCountInput"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter number of offices"
                value={officeCount}
                onChange={(e) => setOfficeCount(e.target.value)}
                min="0"
              />
            </div>

            {/* Main Contact Section */}
            <div className="space-y-2">
              <label htmlFor="mainContactEmailInput" className="block text-sm font-medium text-foreground">
                Main Contact Email
              </label>
              <input
                type="email"
                id="mainContactEmailInput"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter main contact email"
                value={mainContactEmail}
                onChange={(e) => setMainContactEmail(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                This is different from the admin account email and used for main company communications.
              </p>
            </div>

            <div className="space-y-2">
              <label htmlFor="businessUnitsInput" className="block text-sm font-medium text-foreground">
                Business Units / Teams
              </label>
              <div className="relative">
                <div
                  className="flex flex-wrap gap-2 min-h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
                  onClick={() => document.getElementById('business-units-input')?.focus()}
                >
                  {/* Selected business units as badges */}
                  {businessUnits.map((unit) => (
                    <Badge
                      key={unit}
                      variant="secondary"
                      className="flex items-center gap-1 px-2 py-1 text-xs"
                    >
                      {unit}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveBusinessUnit(unit);
                        }}
                        className="ml-1 rounded-full hover:bg-accent"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}

                  {/* Input for adding new business units */}
                  <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                      <div className="flex-1 min-w-[100px]">
                        <input
                          id="business-units-input"
                          type="text"
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value)}
                          onKeyDown={handleKeyDown}
                          onFocus={() => setOpen(true)}
                          placeholder={businessUnits.length === 0 ? "Select or add business units / teams..." : ""}
                          className="w-full bg-transparent border-none outline-none text-sm"
                        />
                      </div>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-[--radix-popover-trigger-width] p-0"
                      align="start"
                      side="bottom"
                      sideOffset={4}
                    >
                      <Command>
                        <CommandInput
                          placeholder="Search business units / teams..."
                          value={inputValue}
                          onValueChange={setInputValue}
                        />
                        <CommandList>
                          <CommandEmpty>No business units / teams found.</CommandEmpty>
                          <CommandGroup>
                            {inputValue.trim() && !availableBusinessUnits.includes(inputValue.trim()) && (
                              <CommandItem
                                onSelect={() => handleAddBusinessUnit(inputValue)}
                                className="cursor-pointer"
                              >
                                <Plus className="mr-2 h-4 w-4" />
                                Add "{inputValue.trim()}"
                              </CommandItem>
                            )}
                            {filteredBusinessUnits.map((unit) => (
                              <CommandItem
                                key={unit}
                                onSelect={() => {
                                  handleAddBusinessUnit(unit);
                                  setOpen(false);
                                }}
                                className="cursor-pointer"
                              >
                                {unit}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {businessUnits.length > 0
                    ? `${businessUnits.length} business unit${businessUnits.length !== 1 ? 's' : ''} / team${businessUnits.length !== 1 ? 's' : ''} selected`
                    : 'Select or add business units / teams for your company'}
                </p>
              </div>
            </div>

            <div className="pt-4 flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                {Object.keys(formErrors).length > 0 && (
                  <p className="text-destructive">Please fix the errors above before saving</p>
                )}
              </div>
              <button
                onClick={handleSaveBusinessDetails}
                disabled={isSaving}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${isSaving
                    ? 'bg-muted text-muted-foreground cursor-not-allowed'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90'
                  }`}
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : 'Save Business Details'}
              </button>
            </div>
          </TabsContent>

          {/* Admin Details Tab Content */}
          <TabsContent value="adminDetails" className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="adminNameInput" className="block text-sm font-medium text-foreground">
                Admin Name
              </label>
              <input
                type="text"
                id="adminNameInput"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter admin name"
                value={adminName}
                onChange={(e) => setAdminName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="adminEmailInput" className="block text-sm font-medium text-foreground">
                Admin Email
              </label>
              <input
                type="email"
                id="adminEmailInput"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter admin email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
              />
            </div>

            <div className="pt-4">
              <Button onClick={handleSaveAdminDetails} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors">
                Save Admin Details
              </Button>
            </div>
          </TabsContent>

          {/* Notification Settings Tab Content */}
          <TabsContent value="notificationSettings" className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="notificationsTypeInput" className="block text-sm font-medium text-foreground">
                Notification Type
              </label>
              <select
                id="notificationsTypeInput"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              >
                <option value="email">Email</option>
                <option value="sms">SMS</option>
                <option value="both">Both Email and SMS</option>
              </select>
            </div>

            <div className="space-y-2">
              <label htmlFor="notificationsEmailInput" className="block text-sm font-medium text-foreground">
                Notification Email
              </label>
              <input
                type="email"
                id="notificationsEmailInput"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder="Enter notification email"
              />
            </div>

            <div className="pt-4">
              <button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors">
                Save Notification Settings
              </button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
